import { useTranslation } from 'react-i18next';
import { Info, MessagesSquare, Sparkles } from 'lucide-react';
import { Badge, Button } from '../ui';
import { isActionable } from './selection';

/**
 * The always-visible action bar for the current selection (spec §9.5: "Jede
 * sinnvolle Auswahl kann erklärt oder eingeprägt werden").
 *
 * It lives in the main panel rather than only in the detail panel so the two
 * actions stay reachable on tablet and mobile, where the detail panel is a
 * drawer that may be closed.
 *
 * @param {object} props
 * @param {import('./selection').KnowledgeSelection|null} props.selection
 * @param {() => void} props.onExplain
 * @param {() => void} props.onEtch
 * @param {() => void} [props.onToggleDetails]
 * @param {boolean} [props.detailsOpen]
 * @param {boolean} [props.showDetailsToggle]
 */
export function SelectionBar({
  selection,
  onExplain,
  onEtch,
  onToggleDetails,
  detailsOpen = false,
  showDetailsToggle = false
}) {
  const { t } = useTranslation();
  const actionable = isActionable(selection);

  return (
    <div className="etch-knowledge__selection-bar">
      <p className="etch-knowledge__selection-label">
        {actionable ? (
          <>
            <Badge>{t(`knowledge.kind.${selection.kind}`, { defaultValue: selection.kind })}</Badge>
            <span className="etch-knowledge__selection-name" title={selection.path || selection.name}>
              {selection.name}
            </span>
          </>
        ) : (
          <span className="etch-knowledge__selection-name">{t('knowledge.noSelection')}</span>
        )}
      </p>

      <div className="etch-knowledge__selection-actions">
        <Button variant="secondary" size="compact" onClick={onExplain} disabled={!actionable}>
          <MessagesSquare size={15} aria-hidden="true" />
          {t('knowledge.explain')}
        </Button>
        <Button variant="primary" size="compact" onClick={onEtch} disabled={!actionable}>
          <Sparkles size={15} aria-hidden="true" />
          {t('knowledge.etchIt')}
        </Button>
        {showDetailsToggle && (
          <Button
            variant="ghost"
            size="compact"
            onClick={onToggleDetails}
            aria-expanded={detailsOpen}
            disabled={!actionable}
          >
            <Info size={15} aria-hidden="true" />
            {t('knowledge.details')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default SelectionBar;
